import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { api } from "../lib/api";
import { Reveal, useSeo } from "../lib/ui";

const ksh = (n) =>
  n >= 1e9 ? `KSh ${(n / 1e9).toFixed(2)}bn` : n >= 1e6 ? `KSh ${(n / 1e6).toFixed(1)}m`
    : n ? `KSh ${Math.round(n).toLocaleString()}` : "Not costed yet";

const Meter = ({ readiness, testId }) => (
  <div className="mt-5" data-testid={testId}>
    <div className="flex items-baseline justify-between">
      <span className="mono text-[0.55rem] uppercase tracking-[0.16em] text-zinc-500">
        {readiness.met} of {readiness.total} conditions met
      </span>
      <span className="mono text-[0.55rem] uppercase tracking-[0.16em] text-ochre">{readiness.pct}%</span>
    </div>
    <div className="mt-2 h-1 w-full bg-zinc-900">
      <div className="h-1 bg-ochre transition-all duration-500" style={{ width: `${readiness.pct}%` }} />
    </div>
  </div>
);

export default function Projects() {
  const [params, setParams] = useSearchParams();
  const [d, setD] = useState(null);
  const stage = params.get("stage") || "";

  useSeo({
    title: "County Projects — KickStartKenyaFootball",
    description:
      "Every county project we hold, with the same eight readiness conditions against it, the risks written down and the ones that stalled still on the page. No money passes through this platform.",
  });

  useEffect(() => {
    api.get(`/projects${stage ? `?stage=${stage}` : ""}`).then((r) => setD(r.data)).catch(() => setD(null));
  }, [stage]);

  if (!d) return <div className="min-h-[60vh] bg-black" data-testid="projects-loading" />;

  return (
    <div className="bg-black pb-24 pt-12 md:pt-16" data-testid="projects-page">
      <div className="mx-auto max-w-[1200px] px-5 md:px-10">
        <p className="overline text-ochre">Land into facilities</p>
        <h1 className="display-1 mt-5 max-w-4xl">{d.heading}</h1>
        <p className="body-copy mt-6 max-w-3xl">{d.intro}</p>

        <div className="mt-10 flex flex-wrap gap-2" data-testid="projects-stage-filter">
          <button type="button" onClick={() => setParams({})} data-testid="projects-stage-all"
            className={`mono border px-4 py-2 text-[0.6rem] uppercase tracking-[0.14em] ${
              stage ? "border-zinc-800 text-zinc-400 hover:text-white" : "border-ochre bg-ochre text-black"}`}>
            All · {d.total}
          </button>
          {d.stages.map((s) => (
            <button key={s.id} type="button" onClick={() => setParams({ stage: s.id })}
              data-testid={`projects-stage-${s.id}`}
              className={`mono border px-4 py-2 text-[0.6rem] uppercase tracking-[0.14em] ${
                stage === s.id ? "border-ochre bg-ochre text-black"
                  : "border-zinc-800 text-zinc-400 hover:text-white"}`}>
              {s.label} · {d.counts[s.id] || 0}
            </button>
          ))}
        </div>

        {d.rows.length ? (
          <div className="mt-10 grid gap-px bg-zinc-800 md:grid-cols-2" data-testid="projects-rows"
            style={d.rows.length === 1 ? { gridTemplateColumns: "minmax(0, 1fr)" } : undefined}>
            {d.rows.map((r, i) => (
              <Reveal key={r.reference} delay={Math.min(i * 0.05, 0.2)}>
                <Link to={`/projects/${r.reference}`} data-testid={`project-card-${r.reference}`}
                  className="group block h-full bg-[#0b0b0b] p-7 transition-colors hover:bg-[#141414]">
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="mono text-[0.55rem] uppercase tracking-[0.16em] text-ochre">{r.stage_label}</span>
                    <span className="mono text-[0.55rem] uppercase tracking-[0.16em] text-zinc-500">
                      {r.county}{r.ward ? ` · ${r.ward}` : ""}
                    </span>
                  </div>
                  <h2 className="mt-4 font-display text-lg uppercase tracking-tight text-white group-hover:text-ochre md:text-xl">
                    {r.title}
                  </h2>
                  {r.summary ? <p className="mt-3 text-sm leading-relaxed text-zinc-400">{r.summary}</p> : null}
                  {r.stage_why ? (
                    <p className="mt-3 border-l-2 border-maasai pl-3 text-xs leading-relaxed text-zinc-400"
                      data-testid={`project-why-${r.reference}`}>
                      {r.stage_why}
                    </p>
                  ) : null}
                  <Meter readiness={r.readiness} testId={`project-meter-${r.reference}`} />
                  <div className="mono mt-5 flex flex-wrap gap-x-6 gap-y-2 text-[0.55rem] uppercase tracking-[0.14em] text-zinc-500">
                    <span>Build {ksh(r.build_cost_shillings)}</span>
                    <span>First year {ksh(r.running_year_shillings)}</span>
                    <span>{r.funding_route}</span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        ) : (
          <p className="mt-10 max-w-2xl border-l-2 border-maasai bg-[#0b0b0b] p-6 text-sm leading-relaxed text-zinc-300"
            data-testid="projects-empty">
            No project has been published at this stage yet. Projects appear here once they have been read, have
            at least one risk written against them, and a county attached. If you know ground standing idle,{" "}
            <Link to="/apply" className="text-ochre underline">tell us about it</Link>.
          </p>
        )}

        <section className="mt-20" data-testid="projects-conditions">
          <p className="eyebrow">The same eight for everybody</p>
          <h2 className="display-3 mt-4">What Ready Actually Means</h2>
          <p className="mt-6 max-w-3xl text-sm leading-relaxed text-zinc-400">
            A project is not ready because it is exciting. It is ready when all eight of these are done, dated
            and on the page. Seven out of eight is not ready.
          </p>
          <div className="mt-8 grid gap-px bg-zinc-800 md:grid-cols-2">
            {d.conditions.map((c, i) => (
              <div key={c.id} className="bg-[#0b0b0b] p-6" data-testid={`condition-${c.id}`}>
                <div className="flex items-baseline gap-3">
                  <span className="mono text-[0.6rem] text-ochre">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="text-sm font-semibold text-white md:text-base">{c.label}</h3>
                </div>
                <p className="mt-3 text-xs leading-relaxed text-zinc-400">{c.why}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-20 max-w-3xl border-l-2 border-maasai bg-[#0b0b0b] p-7" data-testid="projects-regulatory">
          <p className="eyebrow">{d.regulatory.heading}</p>
          <ul className="mt-5 space-y-3">
            {d.regulatory.today.map((t, i) => (
              <li key={i} className="flex gap-3 text-sm leading-relaxed text-zinc-300" data-testid={`regulatory-today-${i}`}>
                <Check size={14} className="mt-1 shrink-0 text-ochre" />
                <span>{t}</span>
              </li>
            ))}
          </ul>
          <p className="mt-5 border-t border-white/10 pt-5 text-xs leading-relaxed text-zinc-400"
            data-testid="regulatory-later">
            {d.regulatory.later}
          </p>
        </section>

        <div className="mono mt-12 flex flex-wrap gap-x-8 gap-y-3 text-[0.6rem] uppercase tracking-[0.16em]">
          <Link to="/specimen-heads-of-terms" className="text-ochre hover:text-white" data-testid="projects-to-specimen">
            The specimen heads of terms
          </Link>
          <Link to="/apply" className="text-ochre hover:text-white" data-testid="projects-to-apply">
            Tell us about ground in your county
          </Link>
          <Link to="/land-comparables" className="text-ochre hover:text-white" data-testid="projects-to-comparables">
            Correct our land figures
          </Link>
        </div>
      </div>
    </div>
  );
}
